import queryActionFactory from 'common/actions/queryActionFactory';
import DataV2 from 'common/DataV2';

import type { Idea } from 'common/api/endpoints/idea';
import type { IdeasState } from 'common/reducers/ideas';
import type { QueryState } from 'common/reducers/queryReducerFactory';
export { defaultState } from 'common/reducers/queryReducerFactory';

export type QueryParams =
  | {
      ideaID: string;
    }
  | {
      ideaURLName: string;
    };

const ideasActions = queryActionFactory<Idea, QueryParams>('idea', DataV2.queries.idea);

export const {
  // query helpers
  getQueryKey,

  // action creators
  invalidateQueries: invalidateIdea,
  loadQuery: loadIdea,
  reloadQuery: reloadIdea,
  bulkRemoveAction: bulkRemoveIdeas,

  // action types
  BulkRemoveType,
  BulkLoadedType,
  RequestType,
  RequestMoreType,
  LoadedType,
  LoadedMoreType,
  ErrorType,
  InvalidateType,
} = ideasActions;

export const upsertIdeas = (ideas: Idea[]) => {
  return ideasActions.bulkLoadedAction(
    ideas
      .map((idea) => {
        const idQueryParams = {
          ideaID: idea._id,
        };
        const idQueryKey = getQueryKey(idQueryParams);
        const urlNameQueryParams = {
          ideaURLName: idea.urlName,
        };
        const urlNameQueryKey = getQueryKey(urlNameQueryParams);
        return [
          {
            queryKey: idQueryKey,
            queryParams: idQueryParams,
            queryResponse: {
              cursor: null,
              items: [idea],
              hasNextPage: false,
            },
          },
          {
            queryKey: urlNameQueryKey,
            queryParams: urlNameQueryParams,
            queryResponse: {
              cursor: null,
              items: [idea],
              hasNextPage: false,
            },
          },
        ];
      })
      .flat(),
  );
};

export const removeIdeas = (ideas: Idea[], ideasState: IdeasState) => {
  let allIdeasToRemove = [...ideas];

  // Find all descendant ideas that have any of the deleted ideas in their ancestorIDs
  const deletedIdeaIDs = new Set(ideas.map((idea) => idea._id));
  const descendantIdeasMap = new Map<string, Idea>();

  Object.values(ideasState)
    .filter(
      (value): value is QueryState<Idea, QueryParams> =>
        typeof value === 'object' && value !== null && 'items' in value,
    )
    .forEach((queryState) => {
      if (queryState?.items) {
        queryState.items.forEach((idea: Idea) => {
          if (
            idea.ancestorIDs &&
            idea.ancestorIDs.some((ancestorID) => deletedIdeaIDs.has(ancestorID))
          ) {
            if (!descendantIdeasMap.has(idea._id)) {
              descendantIdeasMap.set(idea._id, idea);
            }
          }
        });
      }
    });

  const descendantIdeas = Array.from(descendantIdeasMap.values());
  allIdeasToRemove = [...ideas, ...descendantIdeas];

  return ideasActions.bulkRemoveAction(
    allIdeasToRemove.map((idea) => {
      const queryParams = {
        ideaID: idea._id,
      };
      const queryKey = getQueryKey(queryParams);
      return {
        queryKey,
        queryParams,
        items: [idea],
      };
    }),
    // We know there is only one item per query, so we can delete the query if it is empty
    true,
  );
};
